import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Scissors, Instagram, MapPin, ChevronRight, Check, Clock, Calendar as CalendarIcon, User, Phone, Mail, Star, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Showtime Shobi Logo Component - centered, no black bg
const Logo = ({ size = "default" }) => {
  const sizeClasses = {
    small: "h-12 w-12",
    default: "h-20 w-20",
    large: "h-32 w-32"
  };
  
  return (
    <div className="flex justify-center">
      <img 
        src="https://customer-assets.emergentagent.com/job_813546ba-6f89-4690-b685-11ada3367721/artifacts/z9lnfavl_BAC50959-5CEB-4AF5-AD5A-3B18BA713ED2.jpeg"
        alt="Showtime Shobi"
        className={cn("object-contain rounded-full", sizeClasses[size])}
        style={{ background: 'transparent' }}
      />
    </div>
  );
};

// Haircut gallery videos
const galleryVideos = [
  {
    url: "https://customer-assets.emergentagent.com/job_quickcut-36/artifacts/jqb549se_3eadabcc1daf4ff2983b6e243055945e.MOV",
    alt: "Fresh cut showcase 1"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_quickcut-36/artifacts/5d0mlbkh_8a38ff6758294d38b087f1a2916cfc89.MOV",
    alt: "Fresh cut showcase 2"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_quickcut-36/artifacts/3w8auqxy_09b3747d23d940d9b55b971e7cb8405e.MOV",
    alt: "Fresh cut showcase 3"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_quickcut-36/artifacts/p03rd3zb_422ef22fbdcc4b0ebca60e5929d27967.MOV",
    alt: "Fresh cut showcase 4"
  },
  {
    url: "https://customer-assets.emergentagent.com/job_quickcut-36/artifacts/nakuxxwb_929d4c4ebbc34d009895e3427983fa18.MOV",
    alt: "Fresh cut showcase 5"
  }
];

// Video Gallery Item Component
const VideoGalleryItem = ({ video, index }) => {
  const videoRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [canPlay, setCanPlay] = useState(true);
  
  const handleMouseEnter = () => {
    if (videoRef.current && isLoaded) {
      videoRef.current.pause();
    }
  };
  
  const handleMouseLeave = () => {
    if (videoRef.current && isLoaded) {
      videoRef.current.play().catch(() => {});
    }
  };

  const handleLoadedData = () => {
    setIsLoaded(true);
    // Try to autoplay
    if (videoRef.current) {
      videoRef.current.play().catch(() => setCanPlay(false));
    }
  };

  const handleError = () => {
    setCanPlay(false);
    setIsLoaded(true);
  };

  const handleClick = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  };
  
  return (
    <div 
      className="aspect-square overflow-hidden rounded-lg cursor-pointer relative group bg-zinc-800"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {/* Loading placeholder */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-zinc-800 z-10">
          <div className="w-8 h-8 border-2 border-zinc-600 border-t-white rounded-full animate-spin" />
        </div>
      )}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        onLoadedData={handleLoadedData}
        onCanPlay={handleLoadedData}
        onError={handleError}
        className={cn(
          "w-full h-full object-cover transition-all duration-500 group-hover:scale-105",
          isLoaded ? "opacity-100" : "opacity-0"
        )}
      >
        <source src={video.url} type="video/quicktime" />
        <source src={video.url} type="video/mp4" />
      </video>
      {/* Hover overlay with play icon */}
      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
        <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
          <div className="w-0 h-0 border-l-[12px] border-l-white border-y-[8px] border-y-transparent ml-1" />
        </div>
      </div>
      {/* Fallback play button if video doesn't autoplay */}
      {isLoaded && !canPlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <div className="w-16 h-16 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
            <div className="w-0 h-0 border-l-[16px] border-l-white border-y-[10px] border-y-transparent ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};

// Testimonials data
const testimonials = [
  {
    name: "Marcus T.",
    text: "Best barber in Markham! Shobi always delivers a clean fade every time. Been coming here for 2 years.",
    rating: 5
  },
  {
    name: "David K.",
    text: "Finally found my go-to barber. The attention to detail is unmatched. Highly recommend!",
    rating: 5
  },
  {
    name: "Jason L.",
    text: "Shobi knows exactly what I want without me having to explain. Premium service every visit.",
    rating: 5
  },
  {
    name: "Ryan P.",
    text: "The scissor cut here is next level. Worth every penny. Clean shop, great vibes.",
    rating: 5
  }
];

const HomePage = () => {
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [step, setStep] = useState(0); // 0: landing, 1: service, 2: barber, 3: datetime, 4: info, 5: confirm
  const [selectedService, setSelectedService] = useState(null);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(null);

  // Fetch services and barbers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, barbersRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/barbers`)
        ]);
        setServices(servicesRes.data);
        setBarbers(barbersRes.data);
        // Auto-select first barber if only one
        if (barbersRes.data.length === 1) {
          setSelectedBarber(barbersRes.data[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load services");
      }
    };
    fetchData();
  }, []);

  // Fetch available slots when barber and date are selected
  useEffect(() => {
    const fetchSlots = async () => {
      if (!selectedBarber || !selectedDate) return;
      
      setLoadingSlots(true);
      setSelectedTime(null); // Reset selected time when date changes
      try {
        const dateStr = selectedDate.toISOString().split("T")[0];
        const response = await axios.get(`${API}/slots/${selectedBarber.id}/${dateStr}`);
        setAvailableSlots(response.data);
      } catch (error) {
        console.error("Error fetching slots:", error);
        toast.error("Failed to load available times");
      } finally {
        setLoadingSlots(false);
      }
    };
    
    fetchSlots();
  }, [selectedBarber, selectedDate]);

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/bookings`, {
        service_id: selectedService.id,
        barber_id: selectedBarber.id,
        date: selectedDate.toISOString().split("T")[0],
        time: selectedTime,
        customer_name: formData.name,
        customer_phone: formData.phone,
        customer_email: formData.email
      });
      
      setBookingConfirmed(response.data);
      setStep(5);
      toast.success("Booking confirmed!");
    } catch (error) {
      console.error("Error creating booking:", error);
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setSubmitting(false);
    }
  };

  const resetBooking = () => {
    setStep(0);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({ name: "", phone: "", email: "" });
    setBookingConfirmed(null);
  };

  const formatDate = (date) => {
    return date?.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric"
    });
  };

  const formatTime = (time) => {
    const [hours, minutes] = time.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  // Landing / Hero Section with Gallery and Testimonials
  if (step === 0) {
    return (
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <header className="glass fixed top-0 left-0 right-0 z-50 border-b border-zinc-800/50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Scissors className="h-5 w-5 text-white" />
              <span className="font-bold text-sm" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>SHOWTIME SHOBI</span>
            </div>
            <div className="flex items-center gap-4">
              <a 
                href="https://www.instagram.com/showtime_shobi"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-white transition-colors"
                data-testid="header-instagram-link"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <Button 
                onClick={() => setStep(1)}
                className="bg-white text-zinc-950 hover:bg-zinc-200 font-semibold"
                data-testid="header-book-now-btn"
              >
                Book Now
              </Button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <main className="flex-1 pt-16">
          {/* Hero */}
          <section className="flex flex-col items-center justify-center px-6 py-16 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-zinc-900 via-zinc-950 to-zinc-950" />
            
            <div className="relative z-10 text-center max-w-lg mx-auto animate-fade-in">
              <div className="mb-6">
                <Logo size="large" />
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                SHOWTIME SHOBI
              </h1>
              
              <p className="text-zinc-400 text-base tracking-widest uppercase mb-2">
                Barbershop
              </p>
              
              <div className="flex items-center justify-center gap-2 text-zinc-500 mb-6">
                <MapPin className="h-4 w-4" />
                <span className="text-sm">Markham & Scarborough</span>
              </div>

              {/* Stats */}
              <div className="flex items-center justify-center gap-6 mb-8 py-4 border-y border-zinc-800">
                <div className="text-center">
                  <p className="text-xl font-bold text-white">500+</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Clients</p>
                </div>
                <div className="h-8 w-px bg-zinc-800" />
                <div className="text-center">
                  <p className="text-xl font-bold text-white">5.0</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Rating</p>
                </div>
                <div className="h-8 w-px bg-zinc-800" />
                <div className="text-center">
                  <p className="text-xl font-bold text-white">5+</p>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Years</p>
                </div>
              </div>

              {/* CTA */}
              <h2 className="text-xl sm:text-2xl font-bold mb-5" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                Book Your Cut in Seconds
              </h2>

              <Button 
                onClick={() => setStep(1)}
                size="lg"
                className="bg-white text-zinc-950 hover:bg-zinc-200 font-bold text-base px-10 py-5 h-auto rounded-full"
                data-testid="hero-book-now-btn"
              >
                Book Now
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </section>

          {/* Gallery Section */}
          <section className="px-4 py-12 bg-zinc-950">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Our Work</p>
                <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  FRESH CUTS
                </h2>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {galleryVideos.map((video, index) => (
                  <VideoGalleryItem key={index} video={video} index={index} />
                ))}
              </div>
              
              <div className="text-center mt-6">
                <a
                  href="https://www.instagram.com/showtime_shobi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors text-sm"
                >
                  <Instagram className="h-4 w-4" />
                  See more on Instagram
                </a>
              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section className="px-4 py-12 bg-zinc-900/50">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Reviews</p>
                <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  WHAT CLIENTS SAY
                </h2>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {testimonials.map((testimonial, index) => (
                  <Card 
                    key={index}
                    className="border-zinc-800 bg-zinc-900/80 hover:border-zinc-700 transition-all"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-center gap-1 mb-3">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                        ))}
                      </div>
                      <Quote className="h-6 w-6 text-zinc-700 mb-2" />
                      <p className="text-zinc-300 text-sm mb-4 leading-relaxed">
                        &ldquo;{testimonial.text}&rdquo;
                      </p>
                      <p className="text-zinc-500 text-sm font-medium">— {testimonial.name}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Services Preview */}
          <section className="px-4 py-12 bg-zinc-950">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <p className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Services</p>
                <h2 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  OUR SERVICES
                </h2>
              </div>
              
              <div className="space-y-2">
                {services.map((service) => (
                  <div 
                    key={service.id}
                    className="flex justify-between items-center py-3 px-4 bg-zinc-900/50 rounded-lg border border-zinc-800"
                  >
                    <div>
                      <p className="font-medium text-white">{service.name}</p>
                      <p className="text-xs text-zinc-500">{service.duration} min</p>
                    </div>
                    <p className="text-lg font-bold text-white">${service.price}</p>
                  </div>
                ))}
              </div>
              
              <div className="text-center mt-6">
                <Button 
                  onClick={() => setStep(1)}
                  className="bg-white text-zinc-950 hover:bg-zinc-200 font-semibold"
                  data-testid="services-book-now-btn"
                >
                  Book an Appointment
                </Button>
              </div>
            </div>
          </section>

          {/* Footer */}
          <footer className="px-4 py-8 border-t border-zinc-800 bg-zinc-950">
            <div className="max-w-6xl mx-auto text-center">
              <Logo size="small" />
              <p className="text-zinc-500 text-sm mt-4 mb-2">Markham & Scarborough, Ontario</p>
              <p className="text-zinc-600 text-xs">Open Daily 10AM - 8PM</p>
              <div className="mt-4">
                <a
                  href="https://www.instagram.com/showtime_shobi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2 border border-zinc-800 rounded-full text-zinc-400 hover:text-white hover:border-zinc-700 transition-all text-sm"
                  data-testid="footer-instagram-link"
                >
                  <Instagram className="h-4 w-4" />
                  Follow @showtime_shobi
                </a>
              </div>
            </div>
          </footer>
        </main>
      </div>
    );
  }

  // Booking Wizard
  return (
    <div className="min-h-screen flex flex-col bg-zinc-950">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-zinc-800/50">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <button 
            onClick={() => step > 1 ? setStep(step - 1) : setStep(0)}
            className="text-zinc-400 hover:text-white transition-colors"
            data-testid="back-button"
          >
            ← Back
          </button>
          <div className="flex items-center gap-2">
            <Scissors className="h-4 w-4 text-white" />
            <span className="font-bold text-xs" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>SHOWTIME SHOBI</span>
          </div>
          <div className="w-12" />
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-md mx-auto w-full px-4 py-4">
        <div className="flex items-center justify-between">
          {["Service", "Date & Time", "Details", "Confirm"].map((label, i) => (
            <div key={label} className="flex items-center">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all",
                step > i + 1 ? "bg-white text-zinc-950" : 
                step === i + 1 ? "bg-zinc-700 text-white border-2 border-white" : 
                "bg-zinc-800 text-zinc-500"
              )}>
                {step > i + 1 ? <Check className="h-4 w-4" /> : i + 1}
              </div>
              {i < 3 && (
                <div className={cn(
                  "h-0.5 w-8 sm:w-12 mx-1",
                  step > i + 1 ? "bg-white" : "bg-zinc-800"
                )} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content */}
      <main className="flex-1 max-w-md mx-auto w-full px-4 pb-24">
        
        {/* Step 1: Select Service */}
        {step === 1 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              SELECT SERVICE
            </h2>
            <div className="space-y-3">
              {services.map((service) => (
                <Card 
                  key={service.id}
                  className={cn(
                    "cursor-pointer transition-all border-zinc-800 bg-zinc-900 hover:border-zinc-600",
                    selectedService?.id === service.id && "border-white bg-zinc-800"
                  )}
                  onClick={() => {
                    setSelectedService(service);
                    // Auto-advance if barber is already selected
                    if (selectedBarber) {
                      setTimeout(() => setStep(3), 200);
                    } else if (barbers.length === 1) {
                      setSelectedBarber(barbers[0]);
                      setTimeout(() => setStep(3), 200);
                    } else {
                      setTimeout(() => setStep(2), 200);
                    }
                  }}
                  data-testid={`service-card-${service.id}`}
                >
                  <CardContent className="p-4 flex justify-between items-center">
                    <div>
                      <h3 className="font-semibold text-white">{service.name}</h3>
                      <p className="text-sm text-zinc-400">{service.description}</p>
                      <p className="text-xs text-zinc-500 mt-1">{service.duration} min</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-white">${service.price}</p>
                      {selectedService?.id === service.id && (
                        <Check className="h-5 w-5 text-white ml-auto mt-1" />
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Select Barber (if multiple) */}
        {step === 2 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              SELECT BARBER
            </h2>
            <div className="space-y-3">
              {barbers.map((barber) => (
                <Card 
                  key={barber.id}
                  className={cn(
                    "cursor-pointer transition-all border-zinc-800 bg-zinc-900 hover:border-zinc-600",
                    selectedBarber?.id === barber.id && "border-white bg-zinc-800"
                  )}
                  onClick={() => {
                    setSelectedBarber(barber);
                    setTimeout(() => setStep(3), 200);
                  }}
                  data-testid={`barber-card-${barber.id}`}
                >
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-zinc-800 overflow-hidden flex-shrink-0">
                      {barber.image_url ? (
                        <img src={barber.image_url} alt={barber.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="h-8 w-8 text-zinc-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white">{barber.name}</h3>
                      <p className="text-sm text-zinc-400">{barber.bio}</p>
                    </div>
                    {selectedBarber?.id === barber.id && (
                      <Check className="h-5 w-5 text-white" />
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Select Date & Time */}
        {step === 3 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              SELECT DATE & TIME
            </h2>
            
            {/* Calendar */}
            <Card className="border-zinc-800 bg-zinc-900 mb-6">
              <CardContent className="p-4 flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return date < today;
                  }}
                  className="rounded-md"
                  data-testid="booking-calendar"
                />
              </CardContent>
            </Card>

            {/* Time Slots */}
            {selectedDate && (
              <div className="animate-fade-in">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-zinc-400" />
                  Available Times
                </h3>
                
                {loadingSlots ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="h-12 bg-zinc-800 rounded-md animate-pulse" />
                    ))}
                  </div>
                ) : availableSlots.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No available times for this date</p>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {availableSlots.map((slot) => (
                      <Button
                        key={slot.time}
                        variant={selectedTime === slot.time ? "default" : "outline"}
                        className={cn(
                          "h-12",
                          !slot.available && "opacity-50 cursor-not-allowed",
                          selectedTime === slot.time ? "bg-white text-zinc-950" : "border-zinc-700 hover:border-zinc-500"
                        )}
                        disabled={!slot.available}
                        onClick={() => setSelectedTime(slot.time)}
                        data-testid={`time-slot-${slot.time}`}
                      >
                        {formatTime(slot.time)}
                      </Button>
                    ))}
                  </div>
                )}

                {selectedTime && (
                  <Button 
                    className="w-full mt-6 bg-white text-zinc-950 hover:bg-zinc-200 font-semibold h-12"
                    onClick={() => setStep(4)}
                    data-testid="continue-to-details-btn"
                  >
                    Continue
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Customer Info */}
        {step === 4 && (
          <div className="animate-slide-up">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              YOUR DETAILS
            </h2>

            {/* Booking Summary */}
            <Card className="border-zinc-800 bg-zinc-900 mb-6">
              <CardContent className="p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Service</span>
                  <span className="text-white font-medium">{selectedService?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Barber</span>
                  <span className="text-white font-medium">{selectedBarber?.name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Date</span>
                  <span className="text-white font-medium">{formatDate(selectedDate)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Time</span>
                  <span className="text-white font-medium">{selectedTime && formatTime(selectedTime)}</span>
                </div>
                <div className="border-t border-zinc-800 pt-2 mt-2 flex justify-between">
                  <span className="text-zinc-400">Total</span>
                  <span className="text-xl font-bold text-white">${selectedService?.price}</span>
                </div>
              </CardContent>
            </Card>

            <form onSubmit={handleSubmitBooking} className="space-y-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10 bg-zinc-900 border-zinc-800 focus:border-zinc-600 h-12"
                    required
                    data-testid="input-name"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Phone Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    type="tel"
                    placeholder="(416) 123-4567"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10 bg-zinc-900 border-zinc-800 focus:border-zinc-600 h-12"
                    required
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 bg-zinc-900 border-zinc-800 focus:border-zinc-600 h-12"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>

              <Button 
                type="submit"
                className="w-full bg-white text-zinc-950 hover:bg-zinc-200 font-semibold h-12 mt-6"
                disabled={submitting}
                data-testid="confirm-booking-btn"
              >
                {submitting ? "Confirming..." : "Confirm Booking"}
              </Button>
            </form>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && bookingConfirmed && (
          <div className="animate-slide-up text-center py-8">
            <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
              <Check className="h-10 w-10 text-emerald-500" />
            </div>
            
            <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              BOOKING CONFIRMED!
            </h2>
            <p className="text-zinc-400 mb-8">See you at your appointment</p>

            <Card className="border-zinc-800 bg-zinc-900 text-left">
              <CardContent className="p-6 space-y-3">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Service</span>
                  <span className="text-white font-medium">{bookingConfirmed.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Barber</span>
                  <span className="text-white font-medium">{bookingConfirmed.barber_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Date</span>
                  <span className="text-white font-medium">{formatDate(new Date(bookingConfirmed.date + "T12:00:00"))}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Time</span>
                  <span className="text-white font-medium">{formatTime(bookingConfirmed.time)}</span>
                </div>
                <div className="border-t border-zinc-800 pt-3 mt-3">
                  <p className="text-sm text-zinc-500">
                    Confirmation sent to {bookingConfirmed.customer_email}
                  </p>
                </div>
              </CardContent>
            </Card>

            <div className="mt-8 space-y-3">
              <Button 
                onClick={resetBooking}
                className="w-full bg-white text-zinc-950 hover:bg-zinc-200 font-semibold h-12"
                data-testid="book-another-btn"
              >
                Book Another Appointment
              </Button>
              
              <a
                href="https://www.instagram.com/showtime_shobi"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full h-12 border border-zinc-800 rounded-md text-zinc-300 hover:text-white hover:border-zinc-700 transition-all"
                data-testid="confirmation-instagram-link"
              >
                <Instagram className="h-5 w-5" />
                <span>Follow @showtime_shobi</span>
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default HomePage;
